import { AlertTriangle, ArrowRight, RefreshCw, ShoppingCart, Users } from 'lucide-react'
import { formatCurrency, formatNumber, formatPercentage } from '@/lib/utils'
import { MainMetricsCards, type MainMetricsCardsProps } from './MainMetricsCards'
import { ConversionsCard } from './ConversionsCard'
import { MetricCard } from './ui/MetricCard'
import { ClientBaseCard } from './ClientBaseCard'
import { DailyTotals, StudioSubscriptionType } from '@/types'
import type { SalesMixBreakdownItem } from '@/hooks/useSalesMixAnalytics'

interface SalesMixCardData {
  factRevenue: number
  planRevenue: number
  factCount: number
  planCount: number
  avgCheck: number
  planAvgCheck: number
  deviationToYesterdayPct: number
}

interface MissingPlanWarning {
  studioNumbers: string[]
  isCreatingZeroPlan?: boolean
  onCreateZeroPlan?: () => void
}

export interface AnalyticsKpiGridProps {
  mainMetrics: MainMetricsCardsProps
  funnelData: { leads: number; appointments: number; visits: number; sales: number }
  planConversions?: { cr1Plan: number; cr2Plan: number; cr3Plan: number }
  clientsTitle: string
  clientsCount?: number
  clientsGrowth?: number
  clientsRetention?: number
  subscribersCount?: number
  subscribersGrowth?: number
  subscribersRetention?: number
  visitorsCount?: number
  visitorsGrowth?: number
  dailyData: DailyTotals[]
  subscriptionType?: StudioSubscriptionType
  newSales: SalesMixCardData
  recurringSales: SalesMixCardData
  autoRenewals: SalesMixCardData
  newSalesBreakdown?: SalesMixBreakdownItem[]
  recurringSalesBreakdown?: SalesMixBreakdownItem[]
  newSalesSharePct: number
  recurringSharePct: number
  autoRenewalsSharePct: number
  refundsAmount: number
  refundsSharePct: number
  missingPlanWarning?: MissingPlanWarning
  currency?: string
}

export function AnalyticsKpiGrid({
  mainMetrics,
  funnelData,
  planConversions,
  clientsTitle,
  clientsCount,
  clientsGrowth,
  clientsRetention,
  subscribersCount,
  subscribersGrowth,
  subscribersRetention,
  visitorsCount,
  visitorsGrowth,
  dailyData,
  subscriptionType = 'REGULAR',
  newSales,
  recurringSales,
  autoRenewals,
  newSalesBreakdown = [],
  recurringSalesBreakdown = [],
  newSalesSharePct,
  recurringSharePct,
  autoRenewalsSharePct,
  refundsAmount,
  refundsSharePct,
  missingPlanWarning,
  currency = 'RUB'
}: AnalyticsKpiGridProps) {
  const showSubscriptionMetrics = subscriptionType === 'SUBSCRIPTION' || subscriptionType === 'MIXED'
  const hasBreakdownData = (item: SalesMixBreakdownItem) => (
    item.factRevenue > 0 ||
    item.planRevenue > 0 ||
    item.factCount > 0 ||
    item.planCount > 0
  )
  const renderBreakdownRows = (items: SalesMixBreakdownItem[]) => (
    items.filter(hasBreakdownData).map((item) => (
      <div key={item.key}>
        {item.label}:{' '}
        <span className="font-semibold">{formatNumber(item.factCount)}</span>
        {item.planCount > 0 && (
          <>
            {' '} / {' '}
            <span className="font-semibold">{formatNumber(item.planCount)}</span>
          </>
        )}
        {' '}· Ср. чек:{' '}
        <span className="font-semibold">{formatCurrency(item.factAvgCheck, currency)}</span>
        {item.planAvgCheck > 0 && (
          <>
            {' '} / {' '}
            <span className="font-semibold">{formatCurrency(item.planAvgCheck, currency)}</span>
          </>
        )}
      </div>
    ))
  )

  // Дозвоны для карточки конверсий. Считаются здесь, а не в страницах: `dailyData` — те же
  // строки выбранного диапазона, из которых собран `funnelData` (на обоих экранах это
  // `selectedDailyData`), поэтому число не может разойтись между студией и сводным экраном.
  // На сводном экране строки приходят уже просуммированными из `get_aggregated_daily_totals` —
  // она отдаёт `calls_count` / `answered_calls_count`, отдельная миграция не нужна.
  const callsData = dailyData.reduce(
    (acc, day) => ({
      calls: acc.calls + (day.calls_count || 0),
      answered: acc.answered + (day.answered_calls_count || 0)
    }),
    { calls: 0, answered: 0 }
  )

  const missingPlanCount = missingPlanWarning?.studioNumbers.length || 0
  const missingPlanLabel = missingPlanCount === 1
    ? `№${missingPlanWarning?.studioNumbers[0]}`
    : missingPlanWarning?.studioNumbers.map((number) => `№${number}`).join(', ')

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {missingPlanCount > 0 && (
        <div className="md:col-span-4 rounded-lg border border-warning/35 bg-warning/10 px-3 py-2.5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div className="min-w-0 text-sm">
                <div className="font-medium text-foreground">
                  Не задан план по {missingPlanCount === 1 ? 'студии' : `${missingPlanCount} студиям`}: {missingPlanLabel}
                </div>
                <div className="text-xs text-muted-foreground">
                  Факт этих студий уже включён в аналитику. Чтобы убрать предупреждение, сохраните план 0 руб по выручке за период.
                </div>
              </div>
            </div>
            {missingPlanWarning?.onCreateZeroPlan && (
              <button
                type="button"
                onClick={missingPlanWarning.onCreateZeroPlan}
                disabled={missingPlanWarning.isCreatingZeroPlan}
                className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-warning/40 bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-warning/15 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {missingPlanWarning.isCreatingZeroPlan ? 'Открываем...' : 'Перейти к планированию'}
              </button>
            )}
          </div>
        </div>
      )}

      <MainMetricsCards {...mainMetrics} />

      <ConversionsCard
        funnelData={funnelData}
        planConversions={planConversions}
        callsData={callsData}
      />

      <ClientBaseCard
        dailyData={dailyData}
        clientsTitle={clientsTitle}
        clientsCount={clientsCount}
        clientsGrowth={clientsGrowth}
        clientsRetention={clientsRetention}
        subscribersCount={subscribersCount}
        subscribersGrowth={subscribersGrowth}
        subscribersRetention={subscribersRetention}
        visitorsCount={visitorsCount}
        visitorsGrowth={visitorsGrowth}
        subscriptionType={subscriptionType}
      />

      <MetricCard
        icon={ShoppingCart}
        iconColor="text-primary"
        title="Новые продажи"
        value={
          newSales.planRevenue > 0
            ? `${formatCurrency(newSales.factRevenue, currency)} / ${formatCurrency(newSales.planRevenue, currency)}`
            : formatCurrency(newSales.factRevenue, currency)
        }
        valueColor="text-primary"
        subtitle={
          <div className="text-xs space-y-1">
            <div>
              Отклонение на вчера:{' '}
              <span className={newSales.deviationToYesterdayPct >= 0 ? 'text-success font-semibold' : 'text-error font-semibold'}>
                {newSales.deviationToYesterdayPct >= 0 ? '+' : ''}
                {Math.round(newSales.deviationToYesterdayPct)}%
              </span>
            </div>
            <div>
              Доля от общей выручки:{' '}
              <span className="font-semibold">{formatPercentage(newSalesSharePct)}</span>
            </div>
            <div>
              {renderBreakdownRows(newSalesBreakdown)}
            </div>
          </div>
        }
      />

      <MetricCard
        icon={Users}
        iconColor="text-info"
        title="Продажи постоянным"
        value={
          recurringSales.planRevenue > 0
            ? `${formatCurrency(recurringSales.factRevenue, currency)} / ${formatCurrency(recurringSales.planRevenue, currency)}`
            : formatCurrency(recurringSales.factRevenue, currency)
        }
        valueColor="text-info"
        subtitle={
          <div className="text-xs space-y-1">
            <div>
              Отклонение на вчера:{' '}
              <span className={recurringSales.deviationToYesterdayPct >= 0 ? 'text-success font-semibold' : 'text-error font-semibold'}>
                {recurringSales.deviationToYesterdayPct >= 0 ? '+' : ''}
                {Math.round(recurringSales.deviationToYesterdayPct)}%
              </span>
            </div>
            <div>
              Доля от общей выручки:{' '}
              <span className="font-semibold">{formatPercentage(recurringSharePct)}</span>
            </div>
            <div>
              {renderBreakdownRows(recurringSalesBreakdown)}
            </div>
          </div>
        }
      />

      {showSubscriptionMetrics && (
        <MetricCard
          icon={RefreshCw}
          iconColor="text-muted-foreground"
          title="Автопродления подписок"
          value={
            autoRenewals.planRevenue > 0
              ? `${formatCurrency(autoRenewals.factRevenue, currency)} / ${formatCurrency(autoRenewals.planRevenue, currency)}`
              : formatCurrency(autoRenewals.factRevenue, currency)
          }
          valueColor="text-foreground"
          subtitle={
            <div className="text-xs space-y-1">
              <div>
                Отклонение на вчера:{' '}
                <span className={autoRenewals.deviationToYesterdayPct >= 0 ? 'text-success font-semibold' : 'text-error font-semibold'}>
                  {autoRenewals.deviationToYesterdayPct >= 0 ? '+' : ''}
                  {Math.round(autoRenewals.deviationToYesterdayPct)}%
                </span>
              </div>
              <div>
                Доля от общей выручки:{' '}
                <span className="font-semibold">{formatPercentage(autoRenewalsSharePct)}</span>
              </div>
              <div>
                Автопродлений:{' '}
                <span className="font-semibold">{formatNumber(autoRenewals.factCount)}</span>
                {autoRenewals.planCount > 0 && (
                  <>
                    {' '} / {' '}
                    <span className="font-semibold">{formatNumber(autoRenewals.planCount)}</span>
                  </>
                )}
                {' '}· Ср. чек:{' '}
                <span className="font-semibold">{formatCurrency(autoRenewals.avgCheck, currency)}</span>
                {autoRenewals.planAvgCheck > 0 && (
                  <>
                    {' '} / {' '}
                    <span className="font-semibold">{formatCurrency(autoRenewals.planAvgCheck, currency)}</span>
                  </>
                )}
              </div>
            </div>
          }
        />
      )}

      <MetricCard
        icon={ArrowRight}
        iconColor={refundsSharePct > 10 ? 'text-error' : 'text-muted-foreground'}
        title="Возвраты"
        value={formatCurrency(refundsAmount, currency)}
        valueColor={refundsSharePct > 10 ? 'text-error' : 'text-foreground'}
        subtitle={
          <div className="text-xs space-y-1">
            <div>
              Доля от выручки:{' '}
              <span className={refundsSharePct > 10 ? 'text-error font-semibold' : 'font-semibold'}>
                {formatPercentage(refundsSharePct)}
              </span>
            </div>
            <div>Норма возвратов - до 10% от общей выручки.</div>
          </div>
        }
      />
    </div>
  )
}
